import { BASE_URL } from './baseURL';

/**
 * Constants API Service
 * Handles fetching dropdown/select data from /constants endpoints
 */

// API endpoints
const ENDPOINTS = {
  NATIONALITIES: '/constants/nationalities',
};

/**
 * Fetch nationalities list from API
 * @returns {Promise<Object>} Nationalities response
 */
export const getNationalities = async () => {
  try {
    console.log(`=== Fetching Nationalities ===`);
    console.log(`Endpoint: ${BASE_URL}${ENDPOINTS.NATIONALITIES}`);

    const response = await fetch(`${BASE_URL}${ENDPOINTS.NATIONALITIES}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error(`API Error Response:`, response.status, response.statusText);
      throw new Error(`Failed to fetch nationalities: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log(`=== Nationalities Success ===`);
    console.log(`Fetched ${result?.length || 0} nationalities`);

    // Transform API response to match expected format
    // API returns: { Name: 'CAMBODIAN', Code: 33 }
    // Transform to: { value: 'CAMBODIAN', label: 'CAMBODIAN', code: 33 }
    const toTitleCase = (str) =>
      str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;

    const transformedData = Array.isArray(result)
      ? result.map(item => {
          const rawName = item.Name || item.name || item.value || item.label || item.Nationality;
          const titleName = toTitleCase(rawName);
          return {
            value: titleName,
            label: titleName,
            code: item.Code || item.code || item.id || item.Id,
            label_ar: item.NameAr || item.nameAr || item.label_ar || ''
          };
        })
      : [];

    console.log(`Transformed ${transformedData.length} nationalities`);
    if (transformedData.length > 0) {
      console.log(`Sample transformed data:`, transformedData[0]);
    }

    return {
      success: true,
      data: transformedData
    };

  } catch (error) {
    console.error(`=== Nationalities API Error ===`);
    console.error(`Error:`, error.message);

    return {
      success: false,
      error: error.message || 'Failed to fetch nationalities',
      data: []
    };
  }
};

/**
 * Cache for nationalities to avoid repeated API calls
 */
let nationalitiesCache = null;
let cacheTimestamp = null;
// Cache version — bump this whenever the transform logic changes to invalidate stale cache
const CACHE_VERSION = 2;
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

// The in-memory cache is lost on every reload, so a customer who opens a flow
// in a fresh tab waited on the network for a list that barely changes. The
// same entry is kept in localStorage (same version, same hour) so the list can
// be on screen on the first render. Storage can throw in a private window or
// when full; the network path still works without it.
const STORAGE_KEY = `nationalities_cache_v${CACHE_VERSION}`;

const readStoredNationalities = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (
      Array.isArray(stored?.data) &&
      stored.data.length > 0 &&
      Date.now() - stored.timestamp < CACHE_DURATION
    ) {
      return stored;
    }
  } catch (e) {
    /* unreadable or unavailable: fall back to the network */
  }
  return null;
};

const writeStoredNationalities = (data, timestamp) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ data, timestamp }));
  } catch (e) {
    /* not persisted; the in-memory cache still holds it */
  }
};

/**
 * The saved list, if one is still fresh, for seeding state before any fetch.
 * @returns {Array} Nationalities, or [] when nothing usable is stored
 */
export const getStoredNationalities = () => readStoredNationalities()?.data || [];

/**
 * Fetch nationalities with caching
 * @param {Object} options - Options { forceRefresh: boolean }
 * @returns {Promise<Object>} Nationalities response
 */
export const getNationalitiesCached = async (options = {}) => {
  const { forceRefresh = false } = options;

  // Pick up a list saved by an earlier page load
  if (!forceRefresh && !nationalitiesCache) {
    const stored = readStoredNationalities();
    if (stored) {
      nationalitiesCache = stored.data;
      cacheTimestamp = stored.timestamp;
    }
  }

  // Return cached data if available and not forcing refresh
  if (!forceRefresh && nationalitiesCache && cacheTimestamp) {
    const age = Date.now() - cacheTimestamp;
    if (age < CACHE_DURATION) {
      console.log(`✅ Using cached nationalities (age: ${Math.floor(age / 1000)}s)`);
      return {
        success: true,
        data: nationalitiesCache,
        cached: true
      };
    } else {
      console.log(`❌ Cache expired (age: ${Math.floor(age / 1000)}s)`);
    }
  }

  // Fetch fresh data
  const result = await getNationalities();

  // Cache successful results
  if (result.success && result.data.length > 0) {
    nationalitiesCache = result.data;
    cacheTimestamp = Date.now();
    writeStoredNationalities(nationalitiesCache, cacheTimestamp);
    console.log(`💾 Cached ${result.data.length} nationalities`);
  }

  return result;
};

/**
 * Clear nationalities cache
 */
export const clearNationalitiesCache = () => {
  nationalitiesCache = null;
  cacheTimestamp = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* nothing stored */
  }
  console.log('Nationalities cache cleared');
};

export default {
  getNationalities,
  getNationalitiesCached,
  clearNationalitiesCache
};
